import { useIntl } from 'react-intl';

import {
  CohortCharacteristicsResultType,
  CohortSummaryAttributeResult,
  CohortSummaryDetails,
  DomainType,
} from '@verantos-dev/types-cohorts';

import {
  cohortCharacteristicResultTypeToTabularSectionNameMap,
  ClinicalCharacteristicsDomainOptions,
} from '../constants';
import { sanitizeAgeAtIndexData, sanitizeGenderData } from '../helpers';
import type { CohortCharacteristicsTabularDataRow } from '../types';

export const useCohortCharacteristicsTabularData = ({
  data,
  clinicalCharacteristicsDomainType,
  customDisplayData,
  hasAnyCustomSelection,
}: {
  data: CohortSummaryDetails;
  clinicalCharacteristicsDomainType?: string;
  customDisplayData?: CohortSummaryAttributeResult[];
  hasAnyCustomSelection?: boolean;
}) => {
  const intl = useIntl();

  const formatPercentage = (count: number, percentage: number): string => {
    return `${count.toLocaleString()} (${percentage}%)`;
  };

  let idCounter = 0;
  const result: CohortCharacteristicsTabularDataRow[] = [];

  const addCategoryAndEntries = (
    categoryTitle: string,
    entries?: CohortSummaryAttributeResult[]
  ) => {
    result.push({
      id: idCounter++,
      category: categoryTitle,
      name: '',
      value: '',
    });

    entries?.forEach((entry) => {
      result.push({
        id: idCounter++,
        category: undefined,
        name: entry.attributeName,
        value: formatPercentage(entry.attributeCount, entry.attributePercentage),
      });
    });
  };

  if (clinicalCharacteristicsDomainType) {
    if (hasAnyCustomSelection) {
      if (customDisplayData && customDisplayData.length > 0) {
        addCategoryAndEntries(
          intl.formatMessage({
            id: cohortCharacteristicResultTypeToTabularSectionNameMap[
              clinicalCharacteristicsDomainType as DomainType
            ],
          }),
          customDisplayData
        );
      } else if (data[clinicalCharacteristicsDomainType as DomainType]) {
        addCategoryAndEntries(
          intl.formatMessage({
            id: cohortCharacteristicResultTypeToTabularSectionNameMap[
              clinicalCharacteristicsDomainType as DomainType
            ],
          }),
          data[clinicalCharacteristicsDomainType as DomainType]
        );
      }
    } else {
      const otherDomains = ClinicalCharacteristicsDomainOptions.map((item) => item.value).filter(
        (value) => value !== clinicalCharacteristicsDomainType
      );

      const orderedDomains = [clinicalCharacteristicsDomainType, ...otherDomains];

      orderedDomains.forEach((domain) => {
        if (data[domain as DomainType]) {
          addCategoryAndEntries(
            intl.formatMessage({
              id: cohortCharacteristicResultTypeToTabularSectionNameMap[domain as DomainType],
            }),
            data[domain as DomainType]
          );
        }
      });
    }
  } else {
    // Add data for Age
    if (data.ageAtIndex) {
      addCategoryAndEntries(
        intl.formatMessage({
          id: cohortCharacteristicResultTypeToTabularSectionNameMap[
            CohortCharacteristicsResultType.AGE_AT_INDEX
          ],
        }),
        sanitizeAgeAtIndexData(data.ageAtIndex)
      );
    }

    // Add data for Gender
    if (data.gender) {
      addCategoryAndEntries(
        intl.formatMessage({
          id: cohortCharacteristicResultTypeToTabularSectionNameMap[
            CohortCharacteristicsResultType.GENDER
          ],
        }),
        sanitizeGenderData(data.gender)
      );
    }

    // Add data for Race
    if (data.race) {
      addCategoryAndEntries(
        intl.formatMessage({
          id: cohortCharacteristicResultTypeToTabularSectionNameMap[
            CohortCharacteristicsResultType.RACE
          ],
        }),
        data.race
      );
    }

    // Add data for Ethnicity
    if (data.ethnicity) {
      addCategoryAndEntries(
        intl.formatMessage({
          id: cohortCharacteristicResultTypeToTabularSectionNameMap[
            CohortCharacteristicsResultType.ETHNICITY
          ],
        }),
        data.ethnicity
      );
    }
  }
  return result;
};
